import { isOneOf } from "@shared/validation/is-one-of";
import { literalValues } from "@shared/validation/literal-values";
import { isKeyOf } from "@shared/validation/record-keys";

export const LEAD_QUALITY_TIER = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
  UNQUALIFIED: "unqualified",
} as const;

export type LeadQualityTier =
  (typeof LEAD_QUALITY_TIER)[keyof typeof LEAD_QUALITY_TIER];

export const LEAD_QUALITY_TIERS = literalValues(LEAD_QUALITY_TIER);

export const LEAD_QUALITY_LABEL: Record<LeadQualityTier, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  unqualified: "Unqualified",
};

interface QualityThresholds {
  high: number;
  medium: number;
  low: number;
}

export const PRODUCT_QUALITY_THRESHOLDS: Record<string, QualityThresholds> = {
  pallets: { high: 500, medium: 100, low: 20 },
  "plastic-pallets": { high: 500, medium: 100, low: 20 },
  "gaylord-boxes": { high: 500, medium: 100, low: 20 },
  "ibc-totes": { high: 60, medium: 15, low: 5 },
  "plastic-drums": { high: 80, medium: 20, low: 10 },
  "fiber-drums": { high: 80, medium: 20, low: 10 },
  "metal-drums": { high: 200, medium: 25, low: 10 },
  "cardboard-bales": { high: 100, medium: 20, low: 10 },
  "bulk-bags": { high: 500, medium: 25, low: 10 },
  lumber: { high: 1000, medium: 50, low: 10 },
  "shipping-boxes": { high: 500, medium: 25, low: 10 },
  "wooden-spools": { high: 50, medium: 10, low: 5 },
  "wood-crates": { high: 50, medium: 10, low: 5 },
  "plastic-crates": { high: 500, medium: 25, low: 10 },
  equipment: { high: 5, medium: 1, low: 1 },
  "moving-boxes": { high: 500, medium: 25, low: 10 },
};

export const DEFAULT_QUALITY_THRESHOLDS: QualityThresholds = {
  high: 200,
  medium: 50,
  low: 15,
};

export const MAX_PLAUSIBLE_QUANTITY = 1_000_000;

export const LEAD_QUALITY_VALUE_CENTS: Record<LeadQualityTier, number> = {
  high: 25_000,
  medium: 4_000,
  low: 300,
  unqualified: 0,
};

export function isValidLeadQualityTier(
  value: unknown,
): value is LeadQualityTier {
  return (
    typeof value === "string" && isOneOf(LEAD_QUALITY_TIERS, value)
  );
}

type LeadTypeMultipliers = {
  buy: number;
  sell: number;
  recycle: number;
  waste: number;
  inquiry: number;
};

export const PRODUCT_LEAD_TYPE_VALUE_MULTIPLIERS: Record<
  string,
  LeadTypeMultipliers
> = {
  pallets: { buy: 1.0, sell: 0.4, recycle: 0.15, waste: 0.1, inquiry: 0.3 },
  "plastic-pallets": {
    buy: 1.0,
    sell: 0.4,
    recycle: 0.15,
    waste: 0.1,
    inquiry: 0.3,
  },
  "gaylord-boxes": {
    buy: 0.9,
    sell: 1.0,
    recycle: 0.95,
    waste: 0.3,
    inquiry: 0.5,
  },
  "ibc-totes": {
    buy: 0.6,
    sell: 0.9,
    recycle: 1.0,
    waste: 0.35,
    inquiry: 0.5,
  },
  "plastic-drums": {
    buy: 1.0,
    sell: 0.4,
    recycle: 0.4,
    waste: 0.2,
    inquiry: 0.5,
  },
  "fiber-drums": {
    buy: 1.0,
    sell: 0.3,
    recycle: 0.4,
    waste: 0.2,
    inquiry: 0.5,
  },
  "metal-drums": {
    buy: 1.0,
    sell: 0.5,
    recycle: 0.4,
    waste: 0.2,
    inquiry: 0.5,
  },
  "cardboard-bales": {
    buy: 0.3,
    sell: 1.0,
    recycle: 0.5,
    waste: 0.2,
    inquiry: 0.5,
  },
  "bulk-bags": { buy: 1.0, sell: 0.4, recycle: 0.3, waste: 0.2, inquiry: 0.5 },
  lumber: { buy: 1.0, sell: 0.5, recycle: 0.3, waste: 0.15, inquiry: 0.5 },
  "shipping-boxes": {
    buy: 1.0,
    sell: 0.3,
    recycle: 0.3,
    waste: 0.2,
    inquiry: 0.5,
  },
};

export const DEFAULT_LEAD_TYPE_MULTIPLIERS: LeadTypeMultipliers = {
  buy: 1.0,
  sell: 0.4,
  recycle: 0.4,
  waste: 0.2,
  inquiry: 0.5,
};

export function getLeadTypeValueMultiplier(
  leadType: string | undefined,
  productTypeSlug?: string,
): number {
  const multipliers =
    (productTypeSlug && PRODUCT_LEAD_TYPE_VALUE_MULTIPLIERS[productTypeSlug]) ||
    DEFAULT_LEAD_TYPE_MULTIPLIERS;
  if (!leadType) return multipliers.inquiry;
  return isKeyOf(multipliers, leadType)
    ? multipliers[leadType]
    : multipliers.inquiry;
}

export function meetsLeadNotifyTier(tier: LeadQualityTier | undefined): boolean {
  return tier !== undefined && tier !== LEAD_QUALITY_TIER.UNQUALIFIED;
}
